import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import {
  FileText,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  RefreshCw,
  FolderPlus,
  Folder,
  Filter,
  Lock,
  Globe,
  Building2,
  ChevronDown,
  X,
  Check
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { Switch } from '../../../../components/ui/switch';
import { cn } from '../../../../lib/utils';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'currency', label: 'Currency' },
  { value: 'file', label: 'File' },
  { value: 'json', label: 'JSON' },
];

const SCOPE_OPTIONS = [
  { value: 'platform_only', label: 'Platform Only', icon: Lock, color: 'purple' },
  { value: 'available_to_all', label: 'Available to All Agencies', icon: Globe, color: 'blue' },
  { value: 'seed_into_new', label: 'Seed Into New Agencies', icon: Building2, color: 'emerald' },
  { value: 'agency_editable', label: 'Agency Editable', icon: Edit, color: 'amber' },
  { value: 'locked', label: 'Locked / Read Only', icon: Lock, color: 'red' },
];

const PlatformCustomFieldsPage = () => {
  const { token } = useAuth();
  const [fields, setFields] = useState([]);
  const [objects, setObjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    field_name: '',
    object_type: '',
    field_type: 'text',
    folder_id: null,
    default_value: '',
    is_required: false,
    is_locked: false,
    available_to_agencies: false,
    scope: 'platform_only',
    options: []
  });
  const [newOption, setNewOption] = useState('');
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [fieldsRes, objectsRes, foldersRes] = await Promise.all([
        axios.get(`${API}/platform/settings/data/custom-fields`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/platform/settings/data/objects`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/platform/settings/data/folders?type=custom_fields`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setFields(fieldsRes.data.fields || []);
      setObjects(objectsRes.data.objects || []);
      setFolders(foldersRes.data.folders || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.field_name || !formData.object_type) {
      toast.error('Field name and object type are required');
      return;
    }

    try {
      if (editingField) {
        await axios.put(`${API}/platform/settings/data/custom-fields/${editingField.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Field updated');
      } else {
        await axios.post(`${API}/platform/settings/data/custom-fields`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Field created');
      }
      setShowAddModal(false);
      setEditingField(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save field');
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this field?')) return;
    
    try {
      await axios.delete(`${API}/platform/settings/data/custom-fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete field');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName) return;
    
    try {
      await axios.post(`${API}/platform/settings/data/folders`, {
        name: newFolderName,
        type: 'custom_fields'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder created');
      setShowFolderModal(false);
      setNewFolderName('');
      fetchData();
    } catch (err) {
      toast.error('Failed to create folder');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const resetForm = () => {
    setFormData({
      field_name: '',
      object_type: '',
      field_type: 'text',
      folder_id: null,
      default_value: '',
      is_required: false,
      is_locked: false,
      available_to_agencies: false,
      scope: 'platform_only',
      options: []
    });
    setNewOption('');
  };

  const openEditModal = (field) => {
    setEditingField(field);
    setFormData({
      field_name: field.field_name,
      object_type: field.object_type,
      field_type: field.field_type,
      folder_id: field.folder_id,
      default_value: field.default_value || '',
      is_required: field.is_required,
      is_locked: field.is_locked,
      available_to_agencies: field.available_to_agencies,
      scope: field.scope || 'platform_only',
      options: field.options || []
    });
    setShowAddModal(true);
  };

  const addOption = () => {
    if (newOption && !formData.options.includes(newOption)) {
      setFormData({ ...formData, options: [...formData.options, newOption] });
      setNewOption('');
    }
  };

  const removeOption = (opt) => {
    setFormData({ ...formData, options: formData.options.filter(o => o !== opt) });
  };

  const filteredFields = fields.filter(field => {
    const matchesSearch = field.field_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          field.unique_key?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesObject = !selectedObject || field.object_type === selectedObject;
    const matchesFolder = !selectedFolder || field.folder_id === selectedFolder;
    return matchesSearch && matchesObject && matchesFolder;
  });

  // Group by object type
  const groupedByObject = {};
  filteredFields.forEach(field => {
    if (!groupedByObject[field.object_type]) {
      groupedByObject[field.object_type] = [];
    }
    groupedByObject[field.object_type].push(field);
  });

  const getScopeConfig = (scope) => {
    return SCOPE_OPTIONS.find(s => s.value === scope) || SCOPE_OPTIONS[0];
  };

  return (
    <div className="min-h-screen bg-[#0B1F18] flex" data-testid="platform-custom-fields-page">
      {/* Sidebar */}
      <aside className="w-64 min-h-screen bg-[rgba(10,38,28,0.6)] border-r border-[rgba(142,218,182,0.12)] p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[#93AE9F] uppercase">Folders</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFolderModal(true)}
            className="text-emerald-400 hover:text-emerald-300 p-1 h-auto"
          >
            <FolderPlus className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="space-y-1">
          <button
            onClick={() => { setSelectedFolder(null); setSelectedObject(null); }}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
              !selectedFolder && !selectedObject
                ? 'bg-amber-500/20 text-amber-300'
                : 'text-[#93AE9F] hover:bg-zinc-800'
            )}
          >
            <FileText className="w-4 h-4" />
            All Fields
            <span className="ml-auto text-xs bg-zinc-800 px-2 py-0.5 rounded">{fields.length}</span>
          </button>
          
          {folders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => { setSelectedFolder(folder.id); setSelectedObject(null); }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                selectedFolder === folder.id
                  ? 'bg-amber-500/20 text-amber-300'
                  : 'text-[#93AE9F] hover:bg-zinc-800'
              )}
            >
              <Folder className="w-4 h-4" />
              {folder.name}
            </button>
          ))}
        </div>
        
        <div className="mt-6 pt-6 border-t border-[rgba(142,218,182,0.12)]">
          <h3 className="text-sm font-semibold text-[#93AE9F] uppercase mb-4">By Object</h3>
          <div className="space-y-1">
            {objects.filter(o => o.is_active).map((obj) => {
              const count = fields.filter(f => f.object_type === obj.name).length;
              return (
                <button
                  key={obj.id}
                  onClick={() => { setSelectedObject(obj.name); setSelectedFolder(null); }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                    selectedObject === obj.name
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : 'text-[#93AE9F] hover:bg-zinc-800'
                  )}
                >
                  {obj.name}
                  <span className="ml-auto text-xs bg-zinc-800 px-2 py-0.5 rounded">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Custom Fields</h1>
            <p className="text-[#93AE9F] text-sm">
              Define global fields for platform templates, automations, and workflows
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFolderModal(true)}
              className="border-zinc-600 text-zinc-300"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Add Folder
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setEditingField(null);
                setShowAddModal(true);
              }}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="add-field-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Field
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900/50 border-zinc-700 text-white"
            />
          </div>
          {(selectedObject || selectedFolder) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSelectedObject(null); setSelectedFolder(null); }}
              className="border-zinc-600 text-zinc-400"
            >
              <X className="w-4 h-4 mr-1" />
              Clear Filter
            </Button>
          )}
        </div>

        {/* Fields Table */}
        <div className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : filteredFields.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Custom Fields</h3>
              <p className="text-[#93AE9F] text-sm mb-4">
                Create custom fields to use in platform templates and automations.
              </p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Field
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-zinc-900/30">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Field Name</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Object</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Unique Key</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Scope</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Created</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(142,218,182,0.08)]">
                {filteredFields.map((field) => {
                  const scopeConfig = getScopeConfig(field.scope);
                  const ScopeIcon = scopeConfig.icon;
                  return (
                    <tr key={field.id} className="hover:bg-zinc-900/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{field.field_name}</span>
                          {field.is_system_default && (
                            <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs">System</Badge>
                          )}
                          {field.is_locked && (
                            <Lock className="w-3 h-3 text-zinc-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-0">{field.object_type}</Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#93AE9F] capitalize">
                        {field.field_type.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-zinc-800 text-emerald-400 px-2 py-1 rounded truncate max-w-[200px]">
                            {field.unique_key}
                          </code>
                          <button
                            onClick={() => copyToClipboard(field.unique_key)}
                            className="p-1 hover:bg-zinc-700 rounded"
                          >
                            <Copy className="w-3 h-3 text-zinc-500" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={cn(
                          'border-0 text-xs',
                          scopeConfig.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                          scopeConfig.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                          scopeConfig.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
                          scopeConfig.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        )}>
                          <ScopeIcon className="w-3 h-3 mr-1" />
                          {scopeConfig.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#93AE9F]">
                        {field.created_at ? new Date(field.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
                            <DropdownMenuItem
                              onClick={() => openEditModal(field)}
                              className="text-zinc-300 hover:text-white focus:text-white"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => copyToClipboard(field.unique_key)}
                              className="text-zinc-300 hover:text-white focus:text-white"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Key
                            </DropdownMenuItem>
                            {!field.is_system_default && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(field.id)}
                                className="text-red-400 hover:text-red-300 focus:text-red-300"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="mt-4 text-sm text-[#93AE9F]">
          Showing {filteredFields.length} of {fields.length} fields
        </div>
      </main>

      {/* Add/Edit Field Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Edit Field' : 'Add Custom Field'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Field Name *</label>
              <Input
                value={formData.field_name}
                onChange={(e) => setFormData({ ...formData, field_name: e.target.value })}
                placeholder="e.g., Agency Legal Name"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Object Type *</label>
              <Select
                value={formData.object_type}
                onValueChange={(v) => setFormData({ ...formData, object_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select object type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {objects.filter(o => o.is_active).map((obj) => (
                    <SelectItem key={obj.id} value={obj.name}>{obj.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Field Type</label>
              <Select
                value={formData.field_type}
                onValueChange={(v) => setFormData({ ...formData, field_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Options for dropdown/multi-select */}
            {(formData.field_type === 'dropdown' || formData.field_type === 'multi_select') && (
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Options</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Add option..."
                    className="bg-zinc-800 border-zinc-700 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && addOption()}
                  />
                  <Button onClick={addOption} size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.options.map((opt, idx) => (
                    <Badge key={idx} className="bg-zinc-700 text-white pr-1">
                      {opt}
                      <button onClick={() => removeOption(opt)} className="ml-2 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Default Value</label>
              <Input
                value={formData.default_value}
                onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                placeholder="Optional default value"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Scope</label>
              <Select
                value={formData.scope}
                onValueChange={(v) => setFormData({ ...formData, scope: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {SCOPE_OPTIONS.map((scope) => (
                    <SelectItem key={scope.value} value={scope.value}>{scope.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">Required Field</label>
                  <p className="text-xs text-zinc-500">This field must have a value</p>
                </div>
                <Switch
                  checked={formData.is_required}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_required: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">Locked</label>
                  <p className="text-xs text-zinc-500">Agencies cannot modify this field</p>
                </div>
                <Switch
                  checked={formData.is_locked}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_locked: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">Available to Agencies</label>
                  <p className="text-xs text-zinc-500">Agencies can use this field in their templates</p>
                </div>
                <Switch
                  checked={formData.available_to_agencies}
                  onCheckedChange={(checked) => setFormData({ ...formData, available_to_agencies: checked })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setEditingField(null);
                resetForm();
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {editingField ? 'Save Changes' : 'Create Field'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Folder Modal */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium text-zinc-400 mb-2">Folder Name</label>
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="e.g., Client Fields"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowFolderModal(false);
                setNewFolderName('');
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformCustomFieldsPage;
